import { API } from "../constants/api";
import useAuthAxios from "../utils/baseAxios";
import { useQuery } from "@tanstack/react-query";

export type LimitLevel = {
  level: 1 | 2 | 3;
  trading: string;
  tradingRaw: number;
  withdrawal: string;
  withdrawalRaw: number;
};

function formatLimit(n: number): string {
  if (n >= 1000) {
    const k = n / 1000;
    return (k % 1 === 0 ? k.toFixed(0) : k.toFixed(1)) + "K";
  }
  return n.toString();
}

function transformLimits(data: {
  level1: { tradingLimit: number; withdrawalLimit: number };
  level2: { tradingLimit: number; withdrawalLimit: number };
  level3: { tradingLimit: number; withdrawalLimit: number };
}): LimitLevel[] {
  return [
    {
      level: 1,
      tradingRaw: data.level1.tradingLimit,
      trading: formatLimit(data.level1.tradingLimit),
      withdrawalRaw: data.level1.withdrawalLimit,
      withdrawal: formatLimit(data.level1.withdrawalLimit),
    },
    {
      level: 2,
      tradingRaw: data.level2.tradingLimit,
      trading: formatLimit(data.level2.tradingLimit),
      withdrawalRaw: data.level2.withdrawalLimit,
      withdrawal: formatLimit(data.level2.withdrawalLimit),
    },
    {
      level: 3,
      tradingRaw: data.level3.tradingLimit,
      trading: formatLimit(data.level3.tradingLimit),
      withdrawalRaw: data.level3.withdrawalLimit,
      withdrawal: formatLimit(data.level3.withdrawalLimit),
    },
  ];
}

const useLimits = () => {
  const axiosInstance = useAuthAxios();

  const getLimits = async () => {
    const response = await axiosInstance.get(API.getLimits);
    return response.data.data;
  };

  const { data: rawLimits, isLoading, error } = useQuery({
    queryKey: ["get-limits"],
    queryFn: getLimits,
    retry: 1,
  });

  const limits: LimitLevel[] = rawLimits
    ? transformLimits(rawLimits)
    : [
        { level: 1, trading: "1K", tradingRaw: 1000, withdrawal: "100", withdrawalRaw: 100 },
        { level: 2, trading: "10K", tradingRaw: 10000, withdrawal: "500", withdrawalRaw: 500 },
        { level: 3, trading: "25K", tradingRaw: 25000, withdrawal: "1K", withdrawalRaw: 1000 },
      ];

  return { limits, isLoading, error };
};

export default useLimits;
